import { useMemo, useRef } from 'react';

import { EventFilter } from '@shared/types/event';
import { uniqBy } from 'lodash';
import mixpanel from 'mixpanel-browser';

import { useSeriesByTagSlug } from '@queries/TagQueries';

import { useCreateEventMutation, useSubmitFlyerMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import { Screen } from '@components/layout/Screen';

import {
  CreateUpdateEventForm,
  SubmitEventProperties,
} from '@screens/CreateEditEventScreen/CreateEditEventForm';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

interface SeriesAddRsvpEventScreenProps {
  tagSlug: string;
}

const CAN_SUBMIT_UNAUTHENTICATED_EVENT_FLYER = true;

export const SeriesAddRsvpEventScreen = createScreenComponent<
  SeriesAddRsvpEventScreenProps,
  { filter?: EventFilter; tab?: string }
>(({ tagSlug, back, f7route }) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();
  const { series, isFetched } = useSeriesByTagSlug(tagSlug);
  const { isCreatingEvent, createEventAsync } = useCreateEventMutation();
  const { isSubmittingFlyer, submitFlyerAsync } = useSubmitFlyerMutation();
  const didSubmitRef = useRef(false);
  const { tab = 'rsvpToEvents' } = f7route?.query ?? {};

  const cityOptions = useMemo(() => {
    return uniqBy(
      [
        ...(series?.city ? [{ value: series.city._id, label: series.city.name }] : []),
        ...(series?.cities?.map(({ _id: value, name: label }) => ({ value, label })) ?? []),
      ].filter(Boolean),
      'value',
    );
  }, [series]);

  const isSubmitting = isCreatingEvent || isSubmittingFlyer;

  const handleSubmit = async (payload: SubmitEventProperties) => {
    track('Submit Series RSVPEvent', { payload });

    if (CAN_SUBMIT_UNAUTHENTICATED_EVENT_FLYER) {
      if (series) {
        if (didSubmitRef.current) {
          return;
        }

        didSubmitRef.current = true;

        const distinctId = mixpanel.get_distinct_id();

        const { feedEvent } = await submitFlyerAsync({
          distinctId,
          title: payload.title,
          startDate: payload.startDate,
          startTime: payload.startTime,
          base64Image: payload.eventImage,
          seriesId: series?._id,
          venue: {
            _id: payload?.venue?._id,
            title: payload?.venue?.title,
          },
        });

        navigate(`/events/${feedEvent._id}/rsvp`, {
          state: {
            back: back ?? `/noauth/series/${tagSlug}?tab=${tab}`,
          },
        });

        didSubmitRef.current = false;
      }
    }

    createAuthGateHandler(async () => {
      if (didSubmitRef.current) {
        return;
      }

      didSubmitRef.current = true;

      const { event } = await createEventAsync({
        ...payload,
        ...(f7route?.query?.filter ? { filters: [f7route?.query?.filter] } : {}),
        tagIds: series ? [series?._id] : [],
      });

      navigate(`/events/${event._id}/rsvp`, {
        state: {
          back: back ?? `/noauth/series/${tagSlug}?tab=${tab}`,
        },
      });

      didSubmitRef.current = false;
    });
  };

  return (
    <Screen
      name="Series Add RSVP Event Screen"
      headerProps={{ back: back ? { to: back } : undefined, title: 'Add Event' }}
      hideToolbar
    >
      <Screen.Content maxWidth={425}>
        {isFetched && (
          <CreateUpdateEventForm
            hideEntertainerFields
            cityOptions={cityOptions}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        )}
      </Screen.Content>
    </Screen>
  );
});
